// client/src/pages/admin/AdminScanTerms.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * AdminScanTerms — the scan term editor.
 *
 * The vocabulary words, with their yields (from SearchTermResult) and
 * the per-store status. The drop is per store: a term dropped for Aldi
 * stays active for Coles and Woolworths.
 *
 * With a store filter, the Drop sets that store's status; without, the
 * global status.
 */
export default function AdminScanTerms() {
  const { user } = useAuth();
  const [words, setWords] = useState([]);
  const [segment, setSegment] = useState('');
  const [store, setStore] = useState('');
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('count');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selected, setSelected] = useState({});

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchWords = async (sortKey = sortBy, direction = sortDir) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (segment) params.set('segment', segment);
      if (store) params.set('store', store);
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      params.set('sort', sortKey);
      params.set('dir', direction);
      const res = await fetch(`/api/admin/terms?${params}`, { headers: authHeaders() });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed'); }
      const data = await res.json();
      setWords(data.words || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWords(); }, [segment, store, status]);

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const toggleSort = (key) => {
    const newDir = sortBy === key && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(key);
    setSortDir(newDir);
    fetchWords(key, newDir);
  };

  const minEfficiency = (w) => {
    const yields = (w.yields || []).map((y) => y.efficiency || 0);
    if (yields.length === 0) return -1;
    return Math.min(...yields);
  };

  const sortedWords = [...words].sort((a, b) => {
    if (sortBy === 'yield') {
      const aVal = minEfficiency(a);
      const bVal = minEfficiency(b);
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    }
    const aVal = a.count || 0;
    const bVal = b.count || 0;
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
  });

  // The effective status for a word, in the current store context.
  // With a store selected: the store's storeStatus, falling back to the
  // global status. Without: the global status.
  const effectiveStatus = (w) => {
    if (store && w.storeStatus && w.storeStatus[store]) return w.storeStatus[store];
    return w.status || 'active';
  };

  const patchWord = async (id, update, storeContext = null) => {
    try {
      const body = storeContext ? { ...update, store: storeContext } : update;
      const res = await fetch(`/api/admin/terms/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed'); }
      const data = await res.json();

      // Update the local word: either the global status or the per-store.
      setWords((p) =>
        p.map((w) => {
          if (w._id !== id) return w;
          if (storeContext && data.word?.storeStatus) {
            return { ...w, storeStatus: data.word.storeStatus };
          }
          return { ...w, status: update.status };
        })
      );
      setNotice('Saved');
      setTimeout(() => setNotice(null), 2000);
    } catch (err) { setError(err.message); }
  };

  const bulkDrop = async () => {
    if (selectedIds.length === 0) return;
    const scope = store ? `for ${store}` : 'globally';
    if (!window.confirm(`Drop ${selectedIds.length} terms ${scope}?`)) return;
    try {
      // The bulk endpoint only does the global status; with a store
      // selected, the per-store drop is one PATCH each.
      if (store) {
        for (const id of selectedIds) {
          await patchWord(id, { status: 'dropped' }, store);
        }
        setNotice(`${selectedIds.length} terms dropped for ${store}`);
      } else {
        const res = await fetch(`/api/admin/terms/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ ids: selectedIds, status: 'dropped' }),
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed'); }
        const data = await res.json();
        setNotice(`${data.updated} terms dropped globally`);
        await fetchWords();
      }
      setSelected({});
    } catch (err) { setError(err.message); }
  };

  const yieldLabel = (yields) => {
    if (!yields || yields.length === 0) return '—';
    return yields.map((y) => `${y.store}: ${y.products}p/${y.pages}pg (${y.efficiency})`).join(', ');
  };

  const sortIndicator = (key) => {
    if (sortBy !== key) return '';
    return sortDir === 'desc' ? ' ▼' : ' ▲';
  };

  if (!user || user.role !== 'admin') {
    return <div className="text-center py-12 text-gray-500">Admin access only.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-2">Scan terms</h1>
      <p className="text-sm text-gray-500 mb-4">
        {store
          ? `Dropping sets the status for ${store} only. The term stays active for the other stores.`
          : 'Dropping without a store filter sets the status for every store.'}
      </p>

      {notice && <div className="bg-green-50 border border-green-200 text-green-800 p-2 rounded text-sm mb-3">{notice}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm mb-3">{error}</div>}

      <div className="flex gap-2 flex-wrap mb-4">
        <select value={segment} onChange={(e) => setSegment(e.target.value)} className="px-3 py-2 border rounded text-sm">
          <option value="">All segments</option>
          {['pantry','dairy-eggs-fridge','meat-seafood','fruit-vegetables','bakery','deli','frozen','drinks','household','health-beauty','baby','pet','liquor'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={store} onChange={(e) => setStore(e.target.value)} className="px-3 py-2 border rounded text-sm">
          <option value="">All stores (global)</option>
          {['aldi','coles','woolworths'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border rounded text-sm">
          <option value="active">Active</option>
          <option value="dropped">Dropped</option>
          <option value="all">All</option>
        </select>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchWords(); }}
          placeholder="Search words..."
          className="px-3 py-2 border rounded text-sm"
        />
        <button onClick={fetchWords} className="px-3 py-2 bg-gray-100 border rounded text-sm">Refresh</button>
        <button
          onClick={bulkDrop}
          disabled={selectedIds.length === 0}
          className="px-3 py-2 bg-red-100 text-red-700 border border-red-300 rounded text-sm disabled:opacity-40"
        >
          Drop selected {store ? `for ${store}` : 'globally'} ({selectedIds.length})
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm">Loading...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border">
            <thead>
              <tr className="bg-gray-50 border-b text-xs text-gray-600">
                <th className="p-2"></th>
                <th className="p-2 text-left">Word</th>
                <th className="p-2 text-left">Segment</th>
                <th className="p-2 text-right cursor-pointer select-none" onClick={() => toggleSort('count')}>
                  Vocab count{sortIndicator('count')}
                </th>
                <th className="p-2 text-left cursor-pointer select-none" onClick={() => toggleSort('yield')}>
                  Yield (min){sortIndicator('yield')}
                </th>
                <th className="p-2 text-left">Status{store ? ` (${store})` : ''}</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedWords.map((w) => {
                const effStatus = effectiveStatus(w);
                return (
                  <tr key={w._id} className="border-b hover:bg-gray-50">
                    <td className="p-2"><input type="checkbox" checked={!!selected[w._id]} onChange={() => toggle(w._id)} /></td>
                    <td className="p-2 font-medium">{w.word}</td>
                    <td className="p-2 text-gray-600">{w.topCategory || '—'}</td>
                    <td className="p-2 text-right">{w.count || 0}</td>
                    <td className="p-2 text-xs text-gray-600">{yieldLabel(w.yields)}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${effStatus === 'dropped' ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-800'}`}>
                        {effStatus}
                      </span>
                    </td>
                    <td className="p-2">
                      {effStatus !== 'dropped' ? (
                        <button onClick={() => patchWord(w._id, { status: 'dropped' }, store || null)} className="text-xs text-red-600 hover:text-red-800">Drop</button>
                      ) : (
                        <button onClick={() => patchWord(w._id, { status: 'active' }, store || null)} className="text-xs text-green-600 hover:text-green-800">Restore</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
